import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PinInput from '../PinInput/PinInput';
import authService from '../../services/authService';
import { useToast } from '../../contexts/ToastContext';
import { logger } from '../../utils/logger';
import { LockClosedIcon, ShieldCheckIcon } from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface PinDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'verify' | 'set' | 'change';
  onVerify?: (pin: string) => Promise<boolean | void> | void;
  onSuccess?: () => void;
  title?: string;
  description?: string;
}

export default function PinDialog({
  isOpen,
  onClose,
  mode = 'verify',
  onVerify,
  onSuccess,
  title,
  description,
}: PinDialogProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [step, setStep] = useState<
    'enter' | 'confirm' | 'old' | 'new' | 'verify' | 'forgot_otp' | 'forgot_new'
  >('enter');
  const [tempPin, setTempPin] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setError('');
      setTempPin('');
      setOtp('');
      setIsLoading(false);

      if (mode === 'set') {
        setStep('enter'); // Enter new PIN
      } else if (mode === 'change') {
        setStep('old'); // Enter old PIN
      } else {
        setStep('verify'); // Enter PIN to verify
      }
    }
  }, [isOpen, mode]);

  const handlePinComplete = async (pin: string) => {
    setError('');

    if (step === 'forgot_otp') {
      setOtp(pin);
      setStep('forgot_new');
      return;
    }

    if (step === 'forgot_new') {
      // Reset PIN with OTP
      setIsLoading(true);
      try {
        const response = await authService.resetPin(otp, pin);
        if (response.success) {
          showToast('success', t('pin.pin_reset_success') || 'PIN reset successfully');
          // Automatically verify with new PIN or switch to verify?
          // Let's switch to verify to be safe and consistent
          setStep('verify');
        } else {
          setError(response.error || t('pin.pin_reset_failed') || 'Failed to reset PIN');
          setStep('forgot_otp'); // Retry from OTP? Or just new PIN?
          // Usually if failed, maybe OTP was wrong.
          setOtp('');
        }
      } catch (error) {
        logger.error('Reset PIN error:', error);
        setError(t('pin.pin_reset_failed') || 'Failed to reset PIN');
        setStep('forgot_otp');
        setOtp('');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (mode === 'verify' && step === 'verify') {
      if (onVerify) {
        setIsLoading(true);
        try {
          await onVerify(pin);
          // Don't close here, let parent close or handle success
        } catch (err) {
          // Parent should handle error, but if they throw we catch it
          logger.error('PIN verification failed:', err);
          setError(t('pin.pin_verification_failed') || 'PIN verification failed');
          // reset?
        } finally {
          setIsLoading(false);
        }
      }
      return;
    }

    if (mode === 'set') {
      if (step === 'enter') {
        setTempPin(pin);
        setStep('confirm');
        // Clear input effect is handled by unmounting/remounting PinInput based on key or step
      } else if (step === 'confirm') {
        if (pin !== tempPin) {
          setError(t('pin.pin_mismatch') || 'PINs do not match');
          // Optional: go back to step 1 or just clear
          setTempPin('');
          setStep('enter');
          return;
        }

        // Set PIN via API
        setIsLoading(true);
        try {
          const response = await authService.setPin(pin);
          if (response.success) {
            showToast('success', t('pin.pin_set_success') || 'PIN set successfully');
            if (onSuccess) onSuccess();
            onClose();
          } else {
            setError(response.error || t('pin.pin_set_failed') || 'Failed to set PIN');
            setStep('enter'); // Retry
          }
        } catch (error) {
          logger.error('Set PIN error:', error);
          setError(t('pin.pin_set_failed') || 'Failed to set PIN');
          setStep('enter');
        } finally {
          setIsLoading(false);
        }
      }
      return;
    }

    if (mode === 'change') {
      // Implementation for change PIN if needed later (Old -> New -> Confirm)
      // Keeping simple for now
    }
  };

  const handleForgotPin = async () => {
    setIsLoading(true);
    try {
      const response = await authService.requestPinResetOtp();
      if (response.success) {
        showToast('success', t('pin.otp_sent_check_phone') || 'OTP sent to your phone');
        setStep('forgot_otp');
        setError('');
      } else {
        setError(response.error || t('pin.otp_request_failed') || 'Failed to request OTP');
      }
    } catch (error) {
      logger.error('Request OTP error:', error);
      setError(t('pin.otp_request_failed') || 'Failed to request OTP');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTitle = () => {
    if (step === 'forgot_otp') return t('pin.enter_otp') || 'Enter OTP';
    if (step === 'forgot_new') return t('pin.enter_new_pin') || 'Enter New PIN';

    if (title) return title;

    if (mode === 'set') {
      if (step === 'enter') return t('pin.set_pin') || 'Set PIN';
      if (step === 'confirm') return t('pin.confirm_pin') || 'Confirm PIN';
    }
    return t('pin.enter_pin') || 'Enter PIN';
  };

  const getDescription = () => {
    if (step === 'forgot_otp')
      return t('pin.enter_otp_desc') || 'Please enter the OTP sent to your phone';
    if (step === 'forgot_new')
      return t('pin.enter_new_pin_desc') || 'Please enter your new 6-digit PIN';

    if (description) return description;

    if (mode === 'set') {
      if (step === 'enter')
        return t('pin.enter_new_pin_desc') || 'Please enter a 6-digit PIN to secure your wallet';
      if (step === 'confirm')
        return t('pin.confirm_pin_desc') || 'Please re-enter your PIN to confirm';
    }
    return t('pin.enter_pin_desc') || 'Please enter your wallet PIN';
  };

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-md px-4 animate-in fade-in duration-200">
      <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm transform transition-all animate-in zoom-in-95 duration-200 relative overflow-hidden max-h-[85dvh] overflow-y-auto">
        {/* Decorative Header Background */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#04BFBF]/10 to-transparent pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100/80 transition-colors text-gray-400 hover:text-gray-600 z-10"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        <div className="p-8 pt-10 relative">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-16 h-16 bg-[#04BFBF]/10 rounded-full flex items-center justify-center mb-4 shadow-sm">
              {mode === 'set' ? (
                <ShieldCheckIcon className="w-8 h-8 text-[#04BFBF]" />
              ) : (
                <LockClosedIcon className="w-8 h-8 text-[#04BFBF]" />
              )}
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">{getTitle()}</h3>
            <p className="text-gray-500 text-sm max-w-[240px] leading-relaxed">
              {getDescription()}
            </p>
          </div>

          <div className="mb-8">
            {/* Key prop ensures input resets when step changes */}
            <div className="flex justify-center">
              <PinInput
                key={step + (error ? '-error' : '')}
                length={6}
                onComplete={handlePinComplete}
                error={!!error}
                disabled={isLoading}
              />
            </div>
            {error && (
              <div className="flex items-center justify-center mt-4 animate-in slide-in-from-top-1 duration-200">
                <div className="bg-red-50 text-red-600 text-sm px-4 py-2 rounded-lg font-medium border border-red-100 flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-4 h-4 shrink-0"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {error}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {mode === 'verify' && step === 'verify' && (
              <button
                onClick={handleForgotPin}
                className="w-full text-sm text-[#04BFBF] hover:text-[#03a0a0] font-medium transition-colors py-2 hover:bg-[#04BFBF]/5 rounded-lg"
                disabled={isLoading}
              >
                {t('pin.forgot_pin') || 'Forgot PIN?'}
              </button>
            )}

            {(step === 'forgot_otp' || step === 'forgot_new') && (
              <button
                onClick={() => {
                  setStep('verify');
                  setError('');
                }}
                disabled={isLoading}
                className="w-full py-3 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-900 transition-all active:scale-[0.98]"
              >
                {t('back') || 'Back'}
              </button>
            )}
          </div>

          {isLoading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center rounded-3xl z-20">
              <div className="flex flex-col items-center bg-white p-4 rounded-2xl shadow-lg border border-gray-100">
                <svg
                  className="animate-spin h-8 w-8 text-[#04BFBF] mb-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="text-xs font-medium text-gray-500">Please wait...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
